import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ListChecks } from "lucide-react"
import type { SupplierInquiryDetail } from "../../../api/supplierInquiriesApi"
import {
  inquiryScopeView,
  notAskedRegulations,
} from "../../../utils/inquiryScope"
import { SectionLabel } from "../../shared/SectionLabel"
import { InquiryNotAskedNote } from "./InquiryNotAskedNote"
import { InquiryOpenRegulationsNote } from "./InquiryOpenRegulationsNote"
import { InquiryScopePills } from "./InquiryScopePills"

/** What the inquiry asks: the draft's pick or the org's live set, the frozen scope
 *  once sent, or "all" for one sent before per-inquiry scopes; below it, the
 *  regulations its format has no section for, so the gap stays visible after send, and
 *  what a partial answer left open. A Rückfrage without documents shows no pills but
 *  still says what is open: it asked its regulations in the mail (TUR-1732). Renders
 *  nothing when there is neither. */
export function InquiryScopeSection({
  inquiry,
}: {
  inquiry: Pick<
    SupplierInquiryDetail,
    | "askedRegulations"
    | "autoRemindersEffective"
    | "openRegulations"
    | "regulationKeys"
    | "regulationScope"
    | "sentAt"
    | "status"
  >
}) {
  const { translate } = useTranslation()
  const asksDocuments = inquiryScopeView(inquiry, translate).kind !== "none"
  if (!asksDocuments && !inquiry.openRegulations?.length) return null

  return (
    <div className="border-b border-grey4 px-4 py-4">
      <SectionLabel className="mb-2 flex items-center gap-1.5">
        <ListChecks className="h-3.5 w-3.5" />
        {translate("MC_INQ_SCOPE_TITLE")}
      </SectionLabel>
      {asksDocuments && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          <InquiryScopePills inquiry={inquiry} />
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <InquiryOpenRegulationsNote
          openRegulations={inquiry.openRegulations}
          status={inquiry.status}
          autoRemindersEffective={inquiry.autoRemindersEffective}
        />
        <InquiryNotAskedNote
          keys={notAskedRegulations(
            inquiry.askedRegulations,
            inquiry.regulationKeys,
          )}
          askedRegulations={inquiry.askedRegulations}
          // Past tense once it left draft: an inquiry answered without ever being
          // sent (a declaration accepted on the draft) has no send time.
          sent={inquiry.sentAt != null || inquiry.status !== "draft"}
        />
      </div>
    </div>
  )
}
